import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Headset, X, Phone, MessageCircle, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const CALL = "tel:+919608321115";
const WA = "https://wa.me/918100499334";
const HIDE = ["/admin", "/teacher", "/principal", "/virtual-classroom", "/simran-live", "/robot-lab/"];

const FAQ = [
  ["Fees", "फ़ीस", "Annual fees (classroom + lab assistance): Class 6–7 ₹4,999 · Class 8–10 ₹6,999 · Class 11–12 ₹10,999, plus 18% GST. Kits are separate.", "वार्षिक फ़ीस (क्लासरूम + लैब सहायता): कक्षा 6–7 ₹4,999 · 8–10 ₹6,999 · 11–12 ₹10,999, + 18% GST। किट अलग हैं।"],
  ["Coupons", "कूपन", "On the Enroll page tap “Get my coupon” for 50% or 70% off. One per new admission, valid 1 hour or until used, till 14 Nov 2026. Section A (6–8) 1,000 · B (9–10) 500 · C (11–12) 500.", "Enroll पेज पर “कूपन लें” दबाएँ — 50% या 70% छूट। हर नए दाखिले पर एक, 1 घंटे या उपयोग तक मान्य, 14 नवंबर 2026 तक।"],
  ["Admission process", "दाखिला प्रक्रिया", "1) Open Enroll, pick the class. 2) Get a coupon. 3) Pay by UPI QR, card or net banking. 4) Login ID + temporary password arrive by WhatsApp, SMS and email. Change the password on first login.", "1) Enroll खोलें, कक्षा चुनें। 2) कूपन लें। 3) UPI QR/कार्ड/नेट बैंकिंग से भुगतान करें। 4) लॉगिन ID + पासवर्ड WhatsApp, SMS और ईमेल पर।"],
  ["Robo-X trial & plans", "Robo-X ट्रायल", "Robo-X costs ₹3,699 (+GST) on Roboshop and includes 1 month free full access. Then ₹599/month or ₹4,999/year keeps trading unlocked. Manage it at robospark.vip/robox.", "Robo-X Roboshop पर ₹3,699 (+GST), 1 महीना मुफ़्त पूरा एक्सेस। फिर ₹599/माह या ₹4,999/वर्ष। robospark.vip/robox पर मैनेज करें।"],
  ["Kits policy", "किट नीति", "Fees cover classroom + lab assistance only. Kits are not included and can be bought from Roboshop.", "फ़ीस में केवल क्लासरूम + लैब सहायता शामिल है। किट Roboshop से अलग ख़रीदें।"],
  ["Payment status", "भुगतान स्थिति", "Admissions: check the Parent Portal with your receipt number. Roboshop orders: use Track Order. If money was debited but nothing arrived in 30 minutes, call or WhatsApp us.", "दाखिला: रसीद नंबर से Parent Portal देखें। ऑर्डर: Track Order। पैसा कटा पर 30 मिनट में कुछ न मिला तो कॉल/WhatsApp करें।"],
];

const SID = "rs_sweta_sid";
const sweSession = async (fresh) => {
  let s = !fresh && localStorage.getItem(SID);
  if (!s) {
    const r = await fetch(`${API}/sweta/session`, { method: "POST" });
    if (!r.ok) throw new Error(r.status === 429 ? "busy" : "failed");
    s = (await r.json()).session_id;
    localStorage.setItem(SID, s);
  }
  return s;
};

const postChat = async (message, fresh) => fetch(`${API}/sweta/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: await sweSession(fresh), message }) });

async function streamSweta(message, onText) {
  let res = await postChat(message, false);
  if (res.status === 401) res = await postChat(message, true);
  if (!res.ok || !res.body) throw new Error(res.status === 429 ? "busy" : "failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", text = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const evs = buf.split("\n\n");
    buf = evs.pop();
    for (const ev of evs) {
      const line = ev.trim();
      if (!line.startsWith("data:")) continue;
      const d = JSON.parse(line.slice(5));
      if (d.error) throw new Error("failed");
      if (d.t) { text += d.t; onText(text); }
    }
  }
  if (!text.trim()) throw new Error("failed");
}

const AskBox = ({ onAsk, busy }) => {
  const { L } = useLang();
  const [q, setQ] = useState("");
  const submit = (e) => { e.preventDefault(); if (q.trim()) { onAsk(q.trim()); setQ(""); } };
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="sweta-ask-form">
      <Input value={q} onChange={(e) => setQ(e.target.value)} maxLength={800} placeholder={L("Type your question…", "अपना सवाल लिखें…")} className="h-9 bg-white" data-testid="sweta-ask-input" />
      <Button type="submit" disabled={busy || !q.trim()} className="h-9 px-3 bg-[#007A8A] text-white" data-testid="sweta-ask-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
    </form>
  );
};

const Escalate = ({ onDone }) => {
  const { L } = useLang();
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/forms/contact", { ...f, organization: "Sweta Help Desk" }); toast.success(L("Sent! Our support team will reply soon.", "भेजा गया! हमारी टीम जल्द जवाब देगी।")); onDone(); }
    catch { toast.error(L("Could not send. Please call or WhatsApp us.", "भेजा नहीं जा सका। कृपया कॉल/WhatsApp करें।")); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="space-y-2" data-testid="sweta-escalate-form">
      {["name", "email", "phone"].map((k) => <Input key={k} required={k !== "phone"} type={k === "email" ? "email" : "text"} placeholder={L(k[0].toUpperCase() + k.slice(1), { name: "नाम", email: "ईमेल", phone: "फ़ोन" }[k])} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9 bg-white" data-testid={`sweta-input-${k}`} />)}
      <textarea required minLength={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder={L("Describe your question", "अपना सवाल लिखें")} className="w-full rounded-md border border-slate-200 p-2 text-sm h-20" data-testid="sweta-input-message" />
      <Button type="submit" disabled={busy} className="w-full h-9 bg-[#0A192F] text-white" data-testid="sweta-escalate-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />{L("Send to human support", "मानव सहायता को भेजें")}</>}</Button>
    </form>
  );
};

export const SwetaHelpDesk = () => {
  const { L } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [ans, setAns] = useState(null);
  const [human, setHuman] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  if (HIDE.some((p) => pathname.startsWith(p))) return null;
  const ask = async (q) => {
    setAns(null); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "sweta", text: "" }]);
    const put = (text) => setMsgs((m) => [...m.slice(0, -1), { role: "sweta", text }]);
    try { await streamSweta(q, put); }
    catch (e) { put(e.message === "busy" ? L("Many questions at once — please wait a minute.", "एक साथ कई सवाल — कृपया एक मिनट रुकें।") : L("I couldn't answer right now. Pick a topic below or call/WhatsApp us.", "अभी जवाब नहीं दे पाई। नीचे विषय चुनें या कॉल/WhatsApp करें।")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed left-4 bottom-[4.75rem] md:bottom-3 z-50" data-testid="sweta-widget">
      {open && (
        <div className="mb-3 w-[min(22rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-2xl" data-testid="sweta-panel">
          <div className="flex items-center justify-between bg-[#007A8A] text-white px-4 py-3 rounded-t-2xl sticky top-0">
            <div><div className="font-bold">Sweta · {L("Help Desk", "हेल्प डेस्क")}</div><div className="text-[11px] opacity-80">{L("Instant answers · human support on request", "तुरंत जवाब · ज़रूरत पर मानव सहायता")}</div></div>
            <button onClick={() => setOpen(false)} data-testid="sweta-close-btn"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <p className="bg-[#F0F4F8] rounded-xl p-3" data-testid="sweta-answer">{ans ? L(ans[2], ans[3]) : L("Namaste! I'm Sweta. Ask me anything, or pick a topic for an instant answer.", "नमस्ते! मैं श्वेता हूँ। कुछ भी पूछें, या तुरंत जवाब के लिए विषय चुनें।")}</p>
            {msgs.length > 0 && <div className="space-y-2" data-testid="sweta-messages">{msgs.map((m, i) => (
              <div key={i} className={`rounded-xl px-3 py-2 whitespace-pre-line ${m.role === "user" ? "ml-8 bg-[#007A8A] text-white" : "mr-6 bg-[#F0F4F8]"}`} data-testid={`sweta-msg-${m.role}-${i}`}>{m.text || <Loader2 className="w-4 h-4 animate-spin" />}</div>))}</div>}
            <AskBox onAsk={ask} busy={busy} />
            <div className="flex flex-wrap gap-2">{FAQ.map((q, i) => (
              <button key={q[0]} onClick={() => { setAns(q); setHuman(false); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${ans === q ? "bg-[#007A8A] text-white border-[#007A8A]" : "bg-white border-slate-200 hover:border-[#007A8A]"}`} data-testid={`sweta-topic-${i}`}>{L(q[0], q[1])}</button>))}</div>
            <div className="grid grid-cols-2 gap-2">
              <a href={CALL} className="flex items-center justify-center gap-1.5 rounded-xl bg-[#0A192F] text-white py-2.5 font-bold" data-testid="sweta-call-btn"><Phone className="w-4 h-4" />9608321115</a>
              <a href={WA} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 rounded-xl bg-[#25D366] text-white py-2.5 font-bold" data-testid="sweta-whatsapp-btn"><MessageCircle className="w-4 h-4" />8100499334</a>
            </div>
            {human ? <Escalate onDone={() => setHuman(false)} /> : <button onClick={() => setHuman(true)} className="w-full text-xs font-semibold text-[#007A8A] underline" data-testid="sweta-escalate-btn">{L("Didn't help? Talk to a human", "मदद नहीं मिली? इंसान से बात करें")}</button>}
          </div>
        </div>
      )}
      {!open && <button onClick={() => setOpen(true)} className="flex items-center gap-2 rounded-full bg-[#007A8A] text-white pl-3 pr-4 py-2.5 shadow-xl hover:bg-[#006570] transition-colors" data-testid="sweta-open-btn"><Headset className="w-5 h-5" /><span className="font-bold text-sm hidden sm:inline">Sweta · {L("Help Desk", "हेल्प डेस्क")}</span><span className="font-bold text-sm sm:hidden">Sweta</span></button>}
    </div>
  );
};
